<template>
    <SectionViewLayout
      title='settings.imap.title'
      :content_state='content_state'
      content_class="imap-options"
      close_route="settings"
    >
        <template #actions>
            <ActionButton v-if="imap_options_controller.options.allowed"
                @click='confirmModalOpen()'
                icon="fas fa-save"
                v-tooltip='"global.actions.save_changes"'
                :gtm="{
                    module : 'imap_settings',
                    feature: 'imap_form',
                    block  : `top_actions`,
                    action : 'submit'
                }"
            />
        </template>

        <template #content>
            <form>
                <fieldset v-if="AccountController.account.premium.upgrade && !imap_options_controller.options.allowed">
                    <PremiumSubscribe
                        text="premium.imap_disabled"
                        :gtm="{
                            module : 'imap_settings',
                            feature: 'imap_form',
                            block  : `pre_content`,
                            action : 'premium_subscribe'
                        }"
                    />
                </fieldset>

                <fieldset>
                    <legend>
                        {{Language.say('settings.imap.labels.configuration')}}
                    </legend>
                    <p>
                        {{Language.say('settings.imap.imap_description')}}
                    </p>

                    <InputRadio :key="services_enabled"
                      input_name="radio"
                      tag_text="settings.out_of_office."
                      :options="{'deactivated': false, 'activated' : true}"
                      v-model:input_value="services_enabled"
                      :input_disabled="!imap_options_controller.options.allowed"
                    />
                </fieldset>

                <div class="container-alert inline top-space">
                    <div v-if="!imap_options_controller.options.allowed && AccountController.account.premium === 'PREMIUM_NOT_ALLOWED'">
                        <div class="alert warning">
                            <div class="alert-icon">
                                <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
                            </div>
                            <div class="content">
                                {{Language.say('settings.imap.imap_not_allowed')}}
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div class="alert" v-if="imap_options_controller.options.enabled">
                            <div class="alert-icon">
                                <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
                            </div>
                            <div class="content">
                                {{Language.say('settings.imap.imap_notice')}}
                            </div>
                        </div>
                        <div class="alert warning" v-else>
                            <div class="alert-icon">
                                <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
                            </div>
                            <div class="content">
                                {{Language.say('settings.imap.imap_disabled_notice')}}
                            </div>
                        </div>
                    </div>
                </div>
            </form>

            <div class="submit-desktop" v-if="imap_options_controller.options.allowed">
                <ActionButton
                    @click="confirmModalOpen()"
                    class="main-action"
                    icon="fas fa-save"
                    text="global.actions.save_changes"
                    :gtm="{
                        module : 'imap_settings',
                        feature: 'imap_form',
                        block  : `bottom_actions`,
                        action : 'submit'
                    }"
                    />
            </div>

            <ConfirmModal v-if="confirm_warning"
                title='settings.imap.warning_modal.title'
                :content_state="content_state"
                @actionCancel="confirmWarningClose()"
                @actionConfirm="changeSecurityImap()"
            >
                <template #modal_body>
                    <div v-if="services_enabled">
                        <h3>{{Language.say('settings.imap.warning_modal.allow_line1')}}</h3>
                        <p>{{Language.say('settings.imap.warning_modal.allow_line2')}}</p>
                    </div>
                    <div v-else>
                        <h3>{{Language.say('settings.imap.warning_modal.block_line1')}}</h3>
                        <p>{{Language.say('settings.imap.warning_modal.block_line2')}}</p>
                    </div>
                </template>
            </ConfirmModal>
        </template>
    </SectionViewLayout>
</template>

<script setup>
    import { onBeforeMount, ref, reactive, nextTick } from 'vue';
    import SectionViewLayout                from '@/vue/Layouts/Sections/SectionViewLayout.vue';
    import InputRadio                       from '@/vue/Elements/Inputs/InputRadio.vue';
    import ConfirmModal                     from '@/vue/Views/Webmail/Modals/ConfirmModal.vue'
    import ImapOptionsController            from '@/js/Settings/IMAPOptions/IMAPOptionsController.js';
    import ContentState                     from '@/js/Shared/State/ContentState';
    import Language                         from '@/js/Shared/Language/Language.js';
    import AccountController                from '@/js/Webmail/AccountController';
    import PremiumSubscribe                 from '../../Webmail/Partials/Premium/PremiumSubscribe.vue';
    import ActionButton                     from '@/vue/Elements/Buttons/ActionButton.vue';

    const imap_options_controller  = reactive(new ImapOptionsController());
    const confirm_warning          = ref(false);
    const content_state            = reactive(new ContentState());
    const services_enabled         = ref(null);

    content_state.onErrorRetry(async () => {
        await fetchSettings();
    })

    onBeforeMount(async () => {
        await fetchSettings();
        setServicesEnabled();
    })

    const fetchSettings = async () => {
        content_state.setLoading();
        content_state.setLoadedState(
            await imap_options_controller.fetchSettings()
        );
    }

    const confirmModalOpen = () => {
        if(imap_options_controller.options.allowed) {
            confirm_warning.value = true
        }
    }

    const confirmWarningClose = () => {
        confirm_warning.value = false;
    }

    const setServicesEnabled = () => {
        services_enabled.value = imap_options_controller.options.enabled;
    }

    const changeSecurityImap =  async () => {
        content_state.setSavingInline(true);
        let success = await imap_options_controller.changeImap(services_enabled.value);
        content_state.setSavingInline(false);

        if (success) {
            confirmWarningClose();
        }
    }
</script>
